import { useState, useRef, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import logo from "../assets/logo.PNG";
import p1 from "../assets/p1.jpeg";
import p2 from "../assets/p2.jpeg";
import p3 from "../assets/p3.jpeg";
import p4 from "../assets/p4.jpeg";
import p5 from "../assets/p5.jpeg";
import p6 from "../assets/p6.jpeg";
import p7 from "../assets/p7.jpeg";
import zeroRizzImg from "../assets/vcard.jpeg";
import shirt1Img from "../assets/shirt2.jpeg";
import shirt2Img from "../assets/shirt1.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; height: 100%; overflow: hidden; user-select: none; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const slideInR = keyframes`from{opacity:0;transform:translateX(32px)}to{opacity:1;transform:translateX(0)}`;
const slideInL = keyframes`from{opacity:0;transform:translateX(-32px)}to{opacity:1;transform:translateX(0)}`;
const bubbleIn = keyframes`from{opacity:0;transform:scale(0.85) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}`;
const typingDot = keyframes`0%,80%,100%{transform:scale(0.55);opacity:0.4}40%{transform:scale(1);opacity:1}`;
const zeroRizzPop = keyframes`0%{opacity:0;transform:scale(0.6) translateY(20px)}60%{transform:scale(1.06) translateY(-4px)}100%{opacity:1;transform:scale(1) translateY(0)}`;
const vShake = keyframes`0%{transform:rotate(0deg) scale(1)}15%{transform:rotate(-8deg) scale(1.05)}30%{transform:rotate(8deg) scale(1.05)}45%{transform:rotate(-5deg) scale(1.02)}60%{transform:rotate(4deg) scale(1.02)}75%{transform:rotate(-2deg)}100%{transform:rotate(0deg) scale(1)}`;
const vLeftFly = keyframes`0%{opacity:1;transform:rotate(0deg) translateX(0) translateY(0)}100%{opacity:0;transform:rotate(-38deg) translateX(-56px) translateY(-30px)}`;
const vRightFly = keyframes`0%{opacity:1;transform:rotate(0deg) translateX(0) translateY(0)}100%{opacity:0;transform:rotate(38deg) translateX(56px) translateY(-30px)}`;
const vCrackGlow = keyframes`0%,100%{text-shadow:0 0 12px rgba(242,53,110,0.6)}50%{text-shadow:0 0 40px rgba(242,53,110,1),0 0 80px rgba(168,85,253,0.8)}`;
const glowPulse = keyframes`0%,100%{box-shadow:0 12px 56px rgba(116,4,190,0.5),0 0 0 1px rgba(255,255,255,0.05) inset}50%{box-shadow:0 12px 80px rgba(242,53,110,0.5),0 0 0 1px rgba(242,53,110,0.2) inset}`;
const goldGlow = keyframes`0%,100%{box-shadow:0 0 40px rgba(212,160,23,0.4),0 0 80px rgba(212,160,23,0.15),0 0 0 1px rgba(212,160,23,0.3)}50%{box-shadow:0 0 60px rgba(212,160,23,0.65),0 0 120px rgba(212,160,23,0.25),0 0 0 1px rgba(212,160,23,0.5)}`;
const iconHalo = keyframes`0%,100%{box-shadow:0 0 20px rgba(212,160,23,0.5),0 0 40px rgba(212,160,23,0.2)}50%{box-shadow:0 0 40px rgba(212,160,23,0.9),0 0 80px rgba(212,160,23,0.4)}`;

// ─── Screen ───────────────────────────────────────────────────────────────────

const Screen = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  max-width: 430px;
  margin: 0 auto;
  background: linear-gradient(175deg, #7404be 0%, #a855fd 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(
        ellipse 80% 50% at 50% -10%,
        #7404be55 0%,
        transparent 65%
      ),
      radial-gradient(
        ellipse 55% 35% at 15% 100%,
        #a855fd22 0%,
        transparent 60%
      );
  }
`;

// ─── Inbox top bar ────────────────────────────────────────────────────────────

const TopBar = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  width: 100%;
  padding: 54px 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const LogoImg = styled.img`
  width: 40px;
  opacity: 0.9;
`;
const TopTitle = styled.h1`
  font-family: "Mendl", sans-serif;
  font-size: 28px;
  font-weight: 300;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.04em;
`;
const TopRight = styled.div`
  width: 40px;
`;

// ─── Search bar ───────────────────────────────────────────────────────────────

const SearchWrap = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  padding: 16px 20px 0;
`;
const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(12px);
  padding: 0 16px 0 40px;
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #fff;
  outline: none;
  &::placeholder {
    color: rgba(255, 255, 255, 0.38);
  }
`;
const SearchIcon = styled.div`
  position: absolute;
  left: 34px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

// ─── Inbox list ───────────────────────────────────────────────────────────────

const InboxList = styled.div`
  position: relative;
  z-index: 5;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px 0 12px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ThreadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 20px;
  cursor: pointer;
  position: relative;
  background: ${({ $unread }) =>
    $unread ? "rgba(168,85,253,0.1)" : "rgba(0,0,0,0.18)"};
  transition: background 0.15s ease;
  animation: ${fadeUp} ${({ $i }) => 0.18 + $i * 0.06}s
    cubic-bezier(0.16, 1, 0.3, 1) both;
  &:active {
    background: rgba(255, 255, 255, 0.08);
  }
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 76px;
    right: 20px;
    height: 1px;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 54px;
  height: 54px;
`;
const Avatar = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.15);
`;
const OnlineDot = styled.div`
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #4ade80;
  border: 2.5px solid #7404be;
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.7);
`;
const UnreadBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  border-radius: 100px;
  padding: 0 5px;
  background: linear-gradient(135deg, #f2356e, #a855fd);
  border: 2px solid #7404be;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "DM Sans", sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
`;

const ThreadMeta = styled.div`
  flex: 1;
  min-width: 0;
`;
const ThreadTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 3px;
`;
const ThreadName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 0.02em;
`;
const ThreadTime = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  flex-shrink: 0;
  margin-left: 8px;
`;
const ThreadPreview = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: ${({ $unread }) =>
    $unread ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.42)"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${({ $unread }) => ($unread ? 500 : 300)};
`;

// ─── Chat screen ──────────────────────────────────────────────────────────────

const ChatScreen = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  background: linear-gradient(175deg, #0e0020 0%, #060010 60%, #0a0018 100%);
  animation: ${slideInR} 0.32s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

/* radial glow in centre of chat bg */
const ChatBgGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(
      ellipse 70% 40% at 50% 40%,
      rgba(116, 4, 190, 0.18) 0%,
      transparent 70%
    ),
    radial-gradient(
      ellipse 50% 30% at 20% 80%,
      rgba(168, 85, 253, 0.1) 0%,
      transparent 60%
    );
`;

/* Chat top bar */
const ChatTopBar = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  padding: 52px 16px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(13, 0, 24, 0.55);
  backdrop-filter: blur(20px);
`;
const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:active {
    background: rgba(255, 255, 255, 0.12);
  }
`;
const ChatAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.18);
`;
const ChatNameWrap = styled.div`
  flex: 1;
  min-width: 0;
`;
const ChatName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 18px;
  font-weight: 400;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.02em;
`;
const ChatStatus = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: ${({ $online }) => ($online ? "#4ade80" : "rgba(255,255,255,0.38)")};
  letter-spacing: 0.02em;
  margin-top: 1px;
`;
const RizzUpBtn = styled.button`
  height: 34px;
  border-radius: 100px;
  padding: 0 14px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f2356e 0%, #c0305e 100%);
  box-shadow:
    0 3px 14px rgba(242, 53, 110, 0.5),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.03em;
  transition: transform 0.15s ease;
  &:active {
    transform: scale(0.95);
  }
`;

/* Messages scroll area */
const MsgScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 5;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/* Day divider */
const DayDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0;
`;
const DayLine = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;
const DayLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.06em;
  white-space: nowrap;
`;

/* Bubble */
const BubbleRow = styled.div`
  display: flex;
  justify-content: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  align-items: flex-end;
  gap: 8px;
  animation: ${bubbleIn} 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: ${({ $delay }) => $delay || 0}s;
`;
const BubbleAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  margin-bottom: 2px;
`;
const Bubble = styled.div`
  max-width: 72%;
  padding: 10px 14px;
  border-radius: ${({ $mine }) =>
    $mine ? "18px 18px 4px 18px" : "18px 18px 18px 4px"};
  background: ${({ $mine }) =>
    $mine
      ? "linear-gradient(135deg, #9333ea 0%, #f2356e 100%)"
      : "rgba(255,255,255,0.1)"};
  backdrop-filter: ${({ $mine }) => ($mine ? "none" : "blur(12px)")};
  border: ${({ $mine }) =>
    $mine ? "none" : "1px solid rgba(255,255,255,0.1)"};
  box-shadow: ${({ $mine }) =>
    $mine
      ? "0 4px 16px rgba(147,51,234,0.35), 0 1px 0 rgba(255,255,255,0.15) inset"
      : "0 2px 8px rgba(0,0,0,0.3)"};
`;
const BubbleText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #fff;
  line-height: 1.5;
  letter-spacing: 0.01em;
`;
const BubbleTime = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 300;
  color: ${({ $mine }) =>
    $mine ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.35)"};
  margin-top: 4px;
  letter-spacing: 0.02em;
  text-align: ${({ $mine }) => ($mine ? "right" : "left")};
`;

/* Typing indicator */
const TypingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${fadeUp} 0.25s ease both;
`;
const TypingBubble = styled.div`
  padding: 10px 16px;
  border-radius: 18px 18px 18px 4px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 5px;
`;
const TypingDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  animation: ${typingDot} 1.2s ease-in-out infinite;
  animation-delay: ${({ $i }) => $i * 0.18}s;
`;
const TypingAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
`;

/* Input bar */
const InputBar = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  padding: 10px 16px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(13, 0, 24, 0.55);
  backdrop-filter: blur(20px);
`;
const InputIconBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:active {
    background: rgba(255, 255, 255, 0.12);
  }
`;
const MessageInput = styled.input`
  flex: 1;
  height: 40px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(12px);
  padding: 0 16px;
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #fff;
  outline: none;
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;
const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  background: ${({ $hasText }) =>
    $hasText
      ? "linear-gradient(135deg,#9333ea,#f2356e)"
      : "rgba(255,255,255,0.08)"};
  border: 1px solid
    ${({ $hasText }) => ($hasText ? "transparent" : "rgba(255,255,255,0.12)")};
  color: ${({ $hasText }) => ($hasText ? "#fff" : "rgba(255,255,255,0.3)")};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  box-shadow: ${({ $hasText }) =>
    $hasText ? "0 3px 12px rgba(147,51,234,0.45)" : "none"};
  &:active {
    transform: ${({ $hasText }) => ($hasText ? "scale(0.9)" : "none")};
  }
`;

// ─── Rizz Up Modal ───────────────────────────────────────────────────────────

const RizzUpBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  animation: ${fadeUp} 0.22s ease both;
`;
const RizzUpSheet = styled.div`
  width: 100%;
  background: linear-gradient(175deg, #1a0035 0%, #0d0018 100%);
  border-radius: 28px;
  border: 1px solid rgba(168, 85, 253, 0.25);
  box-shadow:
    0 12px 48px rgba(116, 4, 190, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  padding: 36px 28px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;
const RizzUpCloseBtn = styled.button`
  position: absolute;
  top: 18px;
  right: 20px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:active {
    background: rgba(255, 255, 255, 0.14);
  }
`;
const RizzUpIconWrap = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #7404be, #a855fd);
  box-shadow: 0 6px 24px rgba(116, 4, 190, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;
const RizzUpIcon = styled.span`
  font-size: 28px;
  filter: brightness(10);
`;
const RizzUpTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 22px;
  font-weight: 500;
  font-style: italic;
  letter-spacing: 0.02em;
  margin-bottom: 14px;
  background: linear-gradient(135deg, #f2356e, #a855fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;
const RizzUpBody = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
  line-height: 1.6;
  margin-bottom: 10px;
`;
const RizzUpName = styled.span`
  font-weight: 600;
  color: #a855fd;
  -webkit-text-fill-color: #a855fd;
`;
const RizzUpSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  text-align: center;
  line-height: 1.65;
  margin-bottom: 28px;
  max-width: 300px;
`;
const RizzUpConfirmBtn = styled.button`
  width: 100%;
  height: 52px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #f2356e 0%, #7404be 100%);
  box-shadow:
    0 6px 24px rgba(242, 53, 110, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.15) inset;
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  transition: transform 0.18s ease;
  &:active {
    transform: scale(0.97);
  }
`;
const RizzUpCancelBtn = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 100px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.02em;
  transition: background 0.15s ease;
  &:active {
    background: rgba(255, 255, 255, 0.1);
  }
`;

// ─── Success Modal ────────────────────────────────────────────────────────────

const SuccessSheet = styled.div`
  width: 100%;
  background: linear-gradient(175deg, #1a0035 0%, #0d0018 100%);
  border-radius: 28px;
  border: 1px solid rgba(168, 85, 253, 0.3);
  box-shadow:
    0 12px 56px rgba(116, 4, 190, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  padding: 36px 28px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;
const SuccessTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 26px;
  font-weight: 600;
  font-style: italic;
  letter-spacing: 0.02em;
  margin-bottom: 4px;
  background: linear-gradient(135deg, #f2356e, #a855fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;
const SuccessSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 24px;
  letter-spacing: 0.02em;
`;
const SuccessAvatarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
`;
const SuccessAvatarWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  position: relative;
`;
const SuccessAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(242, 53, 110, 0.7);
  box-shadow: 0 0 20px rgba(242, 53, 110, 0.4);
`;
const SuccessAvatarPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: relative;
  background: linear-gradient(135deg, #7404be, #a855fd);
  border: 3px solid rgba(168, 85, 253, 0.7);
  box-shadow: 0 0 20px rgba(168, 85, 253, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SuccessAvatarInitial = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: 30px;
  font-weight: 400;
  color: #fff;
  font-style: italic;
`;
const SuccessAvatarEmoji = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 20px;
`;
const SuccessAvatarName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.02em;
`;
const SuccessAvatarAge = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(168, 85, 253, 0.8);
  letter-spacing: 0.02em;
`;
const SuccessAmpersand = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 40px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.25);
  font-style: italic;
  margin-bottom: 28px;
`;
const SuccessSparkRow = styled.p`
  font-size: 18px;
  letter-spacing: 8px;
  margin-bottom: 10px;
`;
const SuccessParty = styled.div`
  font-size: 48px;
  margin-bottom: 14px;
  filter: drop-shadow(0 4px 12px rgba(168, 85, 253, 0.5));
`;
const SuccessMsg = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.65);
  text-align: center;
  line-height: 1.7;
  margin-bottom: 10px;
  max-width: 280px;
`;
const SuccessEmojis = styled.p`
  font-size: 18px;
  letter-spacing: 6px;
  margin-bottom: 24px;
  opacity: 0.8;
`;
const SuccessBtn = styled.button`
  width: 100%;
  height: 52px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #f2356e 0%, #7404be 100%);
  box-shadow:
    0 6px 24px rgba(242, 53, 110, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.15) inset;
  font-family: "Mendl", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.04em;
  transition: transform 0.18s ease;
  &:active {
    transform: scale(0.97);
  }
`;

// ─── Zero Rizz Modal ─────────────────────────────────────────────────────────────

const ZeroRizzSheet = styled.div`
  width: 100%;
  background: linear-gradient(170deg, #1c1200 0%, #0e0900 55%, #130b00 100%);
  border-radius: 24px;
  border: 1px solid rgba(212, 160, 23, 0.5);
  padding: 40px 28px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  animation:
    ${zeroRizzPop} 0.55s cubic-bezier(0.16, 1, 0.3, 1) both,
    ${goldGlow} 3s ease-in-out 0.6s infinite;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 24px;
    pointer-events: none;
    background: radial-gradient(
      ellipse 80% 45% at 50% 0%,
      rgba(212, 160, 23, 0.14) 0%,
      transparent 70%
    );
  }
`;
const ZeroRizzImgWrap = styled.div`
  position: relative;
  width: 100%;
  height: 140px;
  margin-bottom: 24px;
  border-radius: 14px;
  overflow: visible;
`;
const VImgLeft = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 14px 14px 14px 14px;
  clip-path: polygon(0 0, 50% 0, 50% 100%, 0 100%);
  animation: ${vLeftFly} 0.7s cubic-bezier(0.4, 0, 0.8, 1) 1.1s both;
`;
const VImgRight = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 14px 14px 14px 14px;
  clip-path: polygon(50% 0, 100% 0, 100% 100%, 50% 100%);
  animation: ${vRightFly} 0.7s cubic-bezier(0.4, 0, 0.8, 1) 1.1s both;
`;
const VCrackLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(50% - 1px);
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.95) 30%,
    rgba(242, 53, 110, 1) 50%,
    rgba(255, 255, 255, 0.95) 70%,
    rgba(255, 255, 255, 0) 100%
  );
  border-radius: 2px;
  z-index: 2;
  box-shadow: 0 0 8px 2px rgba(242, 53, 110, 0.7);
  animation: ${fadeUp} 0.15s ease 1s both;
`;
const VShirtRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-bottom: 14px;
`;
const VShirtCard = styled.div`
  flex: 1;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(212, 160, 23, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
`;
const VShirtImg = styled.img`
  width: 100%;
  aspect-ratio: 1/1;
  object-fit: cover;
  object-position: top center;
`;
const VShirtLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 400;
  font-style: italic;
  letter-spacing: 0.06em;
  color: rgba(212, 160, 23, 0.65);
  text-align: center;
  padding: 6px 0;
  background: rgba(0, 0, 0, 0.3);
`;
const VPreorderTag = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  font-style: italic;
  letter-spacing: 0.04em;
  color: rgba(212, 160, 23, 0.55);
  margin-bottom: 16px;
  text-align: center;
`;

const ZeroRizzCloseBtn = styled.button`
  position: absolute;
  top: 18px;
  right: 20px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(212, 160, 23, 0.35);
  background: rgba(212, 160, 23, 0.08);
  backdrop-filter: blur(8px);
  color: rgba(212, 160, 23, 0.7);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:active {
    background: rgba(212, 160, 23, 0.18);
  }
`;
const ZeroRizzTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 22px;
  font-weight: 600;
  font-style: italic;
  letter-spacing: 0.02em;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #f5d060, #c89010);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  animation: ${fadeUp} 0.4s ease 0.5s both;
`;
const ZeroRizzMsg = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(240, 210, 140, 0.75);
  text-align: center;
  line-height: 1.7;
  margin-bottom: 28px;
  max-width: 260px;
  animation: ${fadeUp} 0.4s ease 0.65s both;
`;
const ZeroRizzBtn = styled.button`
  width: 100%;
  height: 52px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #e8a800 0%, #c47d00 50%, #a06000 100%);
  box-shadow:
    0 6px 28px rgba(212, 160, 23, 0.5),
    0 1px 0 rgba(255, 220, 100, 0.35) inset;
  font-family: "Mendl", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #fff3cc;
  letter-spacing: 0.04em;
  transition: transform 0.18s ease;
  animation: ${fadeUp} 0.4s ease 0.8s both;
  &:active {
    transform: scale(0.97);
  }
`;

// ─── Bottom Nav ───────────────────────────────────────────────────────────────

const NavBar = styled.nav`
  position: relative;
  z-index: 10;
  width: 100%;
  flex-shrink: 0;
  padding: 10px 20px 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const NavPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  height: 64px;
  border-radius: 100px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(#3d2253, #1f0e2a);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.22),
    0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
    0 8px 32px rgba(0, 0, 0, 0.45);
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14%;
    right: 14%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    border-radius: 50%;
    pointer-events: none;
  }
`;
const NavItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  padding: 0 4px;
  font-family: "Mendl", sans-serif;
  font-size: 9.5px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  letter-spacing: 0.04em;
  transition: color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  svg {
    font-size: 19px;
  }
  &:active {
    opacity: 0.7;
  }
`;
const NavActiveBg = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $index, $count }) =>
    `calc(${(($index + 0.5) / $count) * 100}% - 28px)`};
  transform: translateY(-50%);
  width: 56px;
  height: 46px;
  border-radius: 20px;
  background: rgba(168, 85, 253, 0.22);
  border: 1px solid rgba(168, 85, 253, 0.35);
  pointer-events: none;
  transition: left 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;
const NavDot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #a855fd;
  margin-top: -1px;
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconMatches = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconFlare = () => (
  <svg viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="currentColor">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
  </svg>
);
const IconMsg = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconHelp = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconBack = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconSearch = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconClip = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const IconVideo = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const IconSend = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconHeart = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

// ─── Data ─────────────────────────────────────────────────────────────────────

const THREADS = [
  {
    id: 1,
    name: "Noah",
    handle: "noah.uk",
    img: p6,
    online: true,
    unread: 2,
    lastMsg: "So why did you like me?",
    lastTime: "1m",
    messages: [
      {
        id: 1,
        mine: false,
        text: "Hey! Matched with you 😊",
        time: "Yesterday",
      },
      {
        id: 2,
        mine: true,
        text: "Hey Noah! How's it going?",
        time: "Yesterday",
      },
      { id: 3, mine: false, text: "What's up?", time: "1 min ago" },
      { id: 4, mine: true, text: "Good stuff", time: "1 min ago" },
      { id: 5, mine: true, text: "What's up with you?", time: "1 min ago" },
      { id: 6, mine: true, text: "😏", time: "1 min ago" },
      {
        id: 7,
        mine: false,
        text: "Do you like my profile photo?",
        time: "1 min ago",
      },
      {
        id: 8,
        mine: true,
        text: "I'm not a feet person",
        time: "less than a minute ago",
      },
      { id: 9, mine: false, text: "Oh", time: "less than a minute ago" },
      {
        id: 10,
        mine: false,
        text: "So why did you like me?",
        time: "less than a minute ago",
      },
    ],
  },
  {
    id: 2,
    name: "Aisha",
    handle: "aisha.ng",
    img: p5,
    online: true,
    unread: 0,
    lastMsg: "Let's grab coffee sometime ☕",
    lastTime: "12m",
    messages: [
      { id: 1, mine: false, text: "Hi! I loved your bio 💜", time: "Today" },
      {
        id: 2,
        mine: true,
        text: "Thank you! Yours is amazing too",
        time: "Today",
      },
      {
        id: 3,
        mine: false,
        text: "Let's grab coffee sometime ☕",
        time: "12 min ago",
      },
    ],
  },
  {
    id: 3,
    name: "Elena",
    handle: "elena.gr",
    img: p7,
    online: false,
    unread: 1,
    lastMsg: "Sailing sounds amazing 🌊",
    lastTime: "1h",
    messages: [
      {
        id: 1,
        mine: true,
        text: "Hey Elena! I saw you love sailing",
        time: "1 hour ago",
      },
      {
        id: 2,
        mine: false,
        text: "Sailing sounds amazing 🌊",
        time: "1 hour ago",
      },
    ],
  },
  {
    id: 4,
    name: "Mia",
    handle: "mia.paris",
    img: p2,
    online: false,
    unread: 0,
    lastMsg: "Paris is always a good idea ✨",
    lastTime: "3h",
    messages: [
      { id: 1, mine: false, text: "Bonjour! 😄", time: "3 hours ago" },
      { id: 2, mine: true, text: "Salut Mia!", time: "3 hours ago" },
      {
        id: 3,
        mine: false,
        text: "Paris is always a good idea ✨",
        time: "3 hours ago",
      },
    ],
  },
  {
    id: 5,
    name: "Amanda",
    handle: "amanda.vita",
    img: p4,
    online: true,
    unread: 0,
    lastMsg: "You: Can't wait to meet you!",
    lastTime: "5h",
    messages: [
      { id: 1, mine: true, text: "Hey Amanda!", time: "5 hours ago" },
      { id: 2, mine: false, text: "Hey!! 🥰", time: "5 hours ago" },
      {
        id: 3,
        mine: true,
        text: "Can't wait to meet you!",
        time: "5 hours ago",
      },
    ],
  },
  {
    id: 6,
    name: "Lucas",
    handle: "lucas.br",
    img: p1,
    online: false,
    unread: 0,
    lastMsg: "Football or pizza — the real debate 🍕",
    lastTime: "1d",
    messages: [
      {
        id: 1,
        mine: false,
        text: "Football or pizza — the real debate 🍕",
        time: "Yesterday",
      },
      {
        id: 2,
        mine: true,
        text: "Easy — pizza while watching football 😂",
        time: "Yesterday",
      },
    ],
  },
];

// ─── Chat View ────────────────────────────────────────────────────────────────

function ChatView({ thread, onBack }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(thread.messages);
  const [typing, setTyping] = useState(false);
  const [showRizzUp, setShowRizzUp] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showZeroRizz, setShowZeroRizz] = useState(false);
  const [zeroRizzStep, setZeroRizzStep] = useState(1);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  function sendMessage() {
    const text = input.trim();
    if (!text) return;
    const newMsg = { id: Date.now(), mine: true, text, time: "just now" };
    setMessages((prev) => [...prev, newMsg]);
    setInput("");

    // Simulate reply after 1.2s with typing indicator
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      const replies = [
        "Haha that's so true 😄",
        "Tell me more...",
        "Interesting 👀",
        "I feel the same way!",
        "You're funny 😂",
        "That's a vibe ✨",
      ];
      const reply = replies[Math.floor(Math.random() * replies.length)];
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, mine: false, text: reply, time: "just now" },
      ]);
    }, 1800);
  }

  function handleKey(e) {
    if (e.key === "Enter") sendMessage();
  }

  // Group messages by day dividers
  const grouped = [];
  let lastDay = null;
  messages.forEach((m, i) => {
    const day = m.time.includes("Yesterday")
      ? "Yesterday"
      : m.time.includes("hour") ||
          m.time.includes("min") ||
          m.time === "just now"
        ? "Today"
        : m.time;
    if (day !== lastDay) {
      grouped.push({ type: "day", label: day });
      lastDay = day;
    }
    grouped.push({ type: "msg", ...m });
  });

  return (
    <ChatScreen>
      <ChatBgGlow />

      {/* Top bar */}
      <ChatTopBar>
        <BackBtn onClick={onBack}>
          <IconBack />
        </BackBtn>
        <ChatAvatar src={thread.img} alt={thread.name} />
        <ChatNameWrap>
          <ChatName>{thread.name}</ChatName>
          <ChatStatus $online={thread.online}>
            {thread.online ? "Active now" : "Offline"}
          </ChatStatus>
        </ChatNameWrap>
        <InputIconBtn title="Video call">
          <IconVideo />
        </InputIconBtn>
        <RizzUpBtn onClick={() => setShowRizzUp(true)}>
          <IconHeart /> Rizz Up?
        </RizzUpBtn>
      </ChatTopBar>

      {/* Messages */}
      <MsgScroll ref={scrollRef}>
        {grouped.map((item, i) => {
          if (item.type === "day")
            return (
              <DayDivider key={`day-${i}`}>
                <DayLine />
                <DayLabel>{item.label}</DayLabel>
                <DayLine />
              </DayDivider>
            );
          return (
            <BubbleRow key={item.id} $mine={item.mine} $delay={0}>
              {!item.mine && (
                <BubbleAvatar src={thread.img} alt={thread.name} />
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: "72%",
                  alignItems: item.mine ? "flex-end" : "flex-start",
                }}
              >
                <Bubble $mine={item.mine}>
                  <BubbleText>{item.text}</BubbleText>
                </Bubble>
                <BubbleTime $mine={item.mine}>{item.time}</BubbleTime>
              </div>
            </BubbleRow>
          );
        })}

        {/* Typing indicator */}
        {typing && (
          <TypingWrap>
            <TypingAvatar src={thread.img} alt="" />
            <TypingBubble>
              <TypingDot $i={0} />
              <TypingDot $i={1} />
              <TypingDot $i={2} />
            </TypingBubble>
          </TypingWrap>
        )}
      </MsgScroll>

      {/* Input bar */}
      <InputBar>
        <InputIconBtn title="Attach">
          <IconClip />
        </InputIconBtn>
        <MessageInput
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
        />
        <SendBtn $hasText={input.trim().length > 0} onClick={sendMessage}>
          <IconSend />
        </SendBtn>
      </InputBar>
      {/* ── Success modal ── */}
      {showSuccess && (
        <RizzUpBackdrop
          onClick={() => {
            setShowSuccess(false);
            setShowZeroRizz(true);
            setZeroRizzStep(1);
          }}
        >
          <SuccessSheet onClick={(e) => e.stopPropagation()}>
            <RizzUpCloseBtn
              onClick={() => {
                setShowSuccess(false);
                setShowZeroRizz(true);
                setZeroRizzStep(1);
              }}
            >
              ✕
            </RizzUpCloseBtn>

            <SuccessTitle>Congratulations</SuccessTitle>
            <SuccessSub>You two linked up 😏</SuccessSub>

            <SuccessAvatarRow>
              <SuccessAvatarWrap>
                <SuccessAvatar src={thread.img} alt={thread.name} />

                <SuccessAvatarName>{thread.name}</SuccessAvatarName>
                <SuccessAvatarAge>Rizz +1</SuccessAvatarAge>
              </SuccessAvatarWrap>

              <SuccessAmpersand>&</SuccessAmpersand>

              <SuccessAvatarWrap>
                <SuccessAvatar
                  src={p4}
                  alt="You"
                  style={{
                    border: "3px solid rgba(168,85,253,0.7)",
                    boxShadow: "0 0 20px rgba(168,85,253,0.4)",
                  }}
                />
                <SuccessAvatarAge>Rizz +1</SuccessAvatarAge>
              </SuccessAvatarWrap>
            </SuccessAvatarRow>

            <SuccessParty>🎊</SuccessParty>

            <SuccessMsg>
              Your Rizz Scores have been updated. Enjoy the memories 😘
            </SuccessMsg>

            <SuccessBtn
              onClick={() => {
                setShowSuccess(false);
                setShowZeroRizz(true);
                setZeroRizzStep(1);
              }}
            >
              Amazing! 👀
            </SuccessBtn>
          </SuccessSheet>
        </RizzUpBackdrop>
      )}

      {/* ── Zero Rizz modal ── */}
      {showZeroRizz && (
        <RizzUpBackdrop onClick={() => zeroRizzStep === 2 && setShowZeroRizz(false)}>
          <ZeroRizzSheet onClick={(e) => e.stopPropagation()}>
            {zeroRizzStep === 2 && (
              <ZeroRizzCloseBtn onClick={() => setShowZeroRizz(false)}>
                ✕
              </ZeroRizzCloseBtn>
            )}

            {zeroRizzStep === 1 ? (
              <>
                <ZeroRizzImgWrap>
                  <VImgLeft src={zeroRizzImg} alt="" />
                  <VImgRight src={zeroRizzImg} alt="" />
                  <VCrackLine />
                </ZeroRizzImgWrap>
                <ZeroRizzTitle>You've just left Zero Rizz behind on NudR</ZeroRizzTitle>
                <ZeroRizzMsg>
                  Your profile no longer displays Zero Rizz and now shows your
                  Rizz Score 😏
                </ZeroRizzMsg>
                <ZeroRizzBtn onClick={() => setZeroRizzStep(2)}>Next →</ZeroRizzBtn>
              </>
            ) : (
              <>
                <ZeroRizzTitle style={{ marginBottom: 6 }}>
                  Don’t keep this to yourself!
                </ZeroRizzTitle>
                <ZeroRizzMsg style={{ marginBottom: 18 }}>
                  A moment this legendary deserves witnesses. Share it with your
                  friends and grab some exclusive Zero Rizz drip.
                </ZeroRizzMsg>
                <VShirtRow>
                  <VShirtCard>
                    <VShirtImg src={shirt1Img} alt="Front shirt" />
                    <VShirtLabel>Front</VShirtLabel>
                  </VShirtCard>
                  <VShirtCard>
                    <VShirtImg src={shirt2Img} alt="Back shirt" />
                    <VShirtLabel>Back</VShirtLabel>
                  </VShirtCard>
                </VShirtRow>
                <VPreorderTag>🎉 Pre-order now — limited drop</VPreorderTag>
                <ZeroRizzBtn onClick={() => setShowZeroRizz(false)}>
                  Pre-order the shirt 👕
                </ZeroRizzBtn>
              </>
            )}
          </ZeroRizzSheet>
        </RizzUpBackdrop>
      )}

      {/* ── Rizz Up modal ── */}
      {showRizzUp && (
        <RizzUpBackdrop onClick={() => setShowRizzUp(false)}>
          <RizzUpSheet onClick={(e) => e.stopPropagation()}>
            <RizzUpCloseBtn onClick={() => setShowRizzUp(false)}>
              ✕
            </RizzUpCloseBtn>
            <RizzUpIconWrap>
              <RizzUpIcon>💜</RizzUpIcon>
            </RizzUpIconWrap>
            <RizzUpTitle>Rizz Up with {thread.name}?</RizzUpTitle>
            <RizzUpBody>
              You're about to send a Rizz Up to{" "}
              <RizzUpName>{thread.name}</RizzUpName> — let them know something
              happened between you two.
            </RizzUpBody>
            <RizzUpSub>
              They'll get a Rizz Confirmation request. Once both of you confirm, both
              your Rizz Scores go up by one. 🔥
            </RizzUpSub>
            <RizzUpConfirmBtn
              onClick={() => {
                setShowRizzUp(false);
                setShowSuccess(true);
              }}
            >
              <IconHeart /> Yes, Confirm Rizz Up
            </RizzUpConfirmBtn>
            <RizzUpCancelBtn onClick={() => setShowRizzUp(false)}>
              Cancel
            </RizzUpCancelBtn>
          </RizzUpSheet>
        </RizzUpBackdrop>
      )}
    </ChatScreen>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MessagesPage({ onNavigate }) {
  const [search, setSearch] = useState("");
  const [openThread, setOpenThread] = useState(null);

  const filtered = THREADS.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalUnread = THREADS.reduce((n, t) => n + t.unread, 0);

  return (
    <>
      <GlobalStyle />
      <Screen>
        {/* ── Inbox ── */}
        <TopBar>
          <LogoImg src={logo} alt="" />
          <TopTitle>Messages</TopTitle>
          <TopRight />
        </TopBar>

        <SearchWrap>
          <SearchIcon>
            <IconSearch />
          </SearchIcon>
          <SearchInput
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrap>

        <InboxList>
          {filtered.length === 0 && (
            <div style={{ padding: "40px 20px", textAlign: "center" }}>
              <p
                style={{
                  fontFamily: "'Mendl',sans-serif",
                  fontSize: 14,
                  color: "rgba(255,255,255,0.3)",
                }}
              >
                No conversations found
              </p>
            </div>
          )}
          {filtered.map((t, i) => (
            <ThreadRow
              key={t.id}
              $i={i}
              $unread={t.unread > 0}
              onClick={() => setOpenThread(t)}
            >
              <AvatarWrap>
                <Avatar src={t.img} alt={t.name} />
                {t.online && <OnlineDot />}
                {t.unread > 0 && <UnreadBadge>{t.unread}</UnreadBadge>}
              </AvatarWrap>
              <ThreadMeta>
                <ThreadTop>
                  <ThreadName>{t.name}</ThreadName>
                  <ThreadTime>{t.lastTime}</ThreadTime>
                </ThreadTop>
                <ThreadPreview $unread={t.unread > 0}>
                  {t.lastMsg}
                </ThreadPreview>
              </ThreadMeta>
            </ThreadRow>
          ))}
        </InboxList>

        {/* Bottom nav */}
        <NavBar>
          <NavPill>
            <NavActiveBg $index={2} $count={5} />
            <NavItem onClick={() => onNavigate?.("matches")}>
              <IconMatches />
              <span>Matches</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("discover")}>
              <IconFlare />
              <span>Discover</span>
            </NavItem>
            <NavItem $active>
              <IconMsg />
              <span>Messages</span>
              <NavDot />
            </NavItem>
            <NavItem onClick={() => onNavigate?.("profile")}>
              <IconUser />
              <span>Profile</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("help")}>
              <IconHelp />
              <span>Help</span>
            </NavItem>
          </NavPill>
        </NavBar>

        {/* ── Chat view slides over inbox ── */}
        {openThread && (
          <ChatView thread={openThread} onBack={() => setOpenThread(null)} />
        )}
      </Screen>
    </>
  );
}
